import { useRef, useState, useCallback } from 'react';
import * as React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { maxBy } from 'lodash-es';
import { tokens } from '@hotjar/hotsauce';
import { Tooltip } from '@hotjar/webapp/components/Tooltip';
import type { Site } from '@hotjar/site';
import type { SegmentWithUnsupportedFeature } from '@hotjar/webapp/features/Recordings/types';
import { css } from '@emotion/react';
import { getMinimumFeatureGroupForFeature } from '@hotjar/webapp/domains/common/feature';
import { FeatureGroupEnum } from '@hotjar/webapp/domains/revenue/plan/types';
import { ScaleTrialBadge } from '@hotjar/recordings/src/features/RecordingsPage/components/Console/ScaleTrialBadge';
import { isAllowed } from '@hotjar/feature-flags';

const Item = styled.div<{ hasButton: boolean }>`
  margin: 0 ${tokens.spacer.x2};
  position: relative;

  &:hover {
    border-radius: 2px;
    padding-right: ${({ hasButton }) => (hasButton ? tokens.spacer.x2pt5 : 'unset')};
  }
`;

const StyledLink = styled(Link)<{ as?: string }>`
  color: ${tokens.color.text_default};
  ${tokens.text('caption')};
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: ${tokens.spacer.x5};
  padding-right: ${tokens.spacer.x1};
  padding-left: ${tokens.spacer.x2};
  border-radius: ${tokens.radius.m};
  cursor: pointer;
  transition: none;
  border: none;
  width: 100%;
  background-color: transparent;

  &:focus,
  &:hover {
    color: ${tokens.color.text_default};
    text-decoration: none;
  }

  &:hover {
    background-color: ${tokens.color.neutral_100};
  }
`;

const selectedStyles = css`
  color: ${tokens.color.text_default};
  ${tokens.text('subtitle3')};
  background-color: ${tokens.color.bg_active} !important;
  border-radius: ${tokens.radius.m};
`;

const disabledStyles = css`
  color: ${tokens.color.text_disabled};

  &:focus,
  &:hover {
    color: ${tokens.color.text_disabled};
  }
`;

const ButtonWrapper = styled.span<{ isVisible: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  background-color: ${tokens.color.ui_background};
  transition: opacity 0.25s ease-in-out;
  /* NOTE: this is to do not cover with the gradient eventual button outline */
  padding-left: 2px;

  &:before {
    content: '';
    position: absolute;
    right: 100%;
    height: 100%;
    width: ${tokens.spacer.x2};
    background-image: linear-gradient(to left, ${tokens.color.white}, rgba(255, 255, 255, 0));
  }
`;

const BagdeSpacer = styled.span`
  margin-left: ${tokens.spacer.x1};
`;

const TruncatedText = styled.span`
  display: inline-block;
  ${tokens.truncateText()}
`;

interface Props {
  segment: SegmentWithUnsupportedFeature;
  site: Site;
  isSelected: boolean;
  renderButton?: () => React.ReactNode;
  domId?: string;
  onUpsellRequest: (featureName: string) => void;
  hasScaleTrialBadge?: boolean;
}

export const SegmentItem = ({
  segment,
  site,
  isSelected,
  renderButton,
  domId = undefined,
  onUpsellRequest,
  hasScaleTrialBadge,
}: Props): JSX.Element => {
  const siteId = (site && site.id) || 1;
  const { t } = useTranslation();
  const textRef = useRef(null);
  const [isItemHovered, setItemHovered] = useState(false);
  const [wasItemRendered, setItemRendered] = useState(false);
  const handleUpsellClicked = useCallback(() => {
    const featureGroupList = segment.unsupported_features.map((feature) => {
      const featureGroupName = getMinimumFeatureGroupForFeature(feature);
      return {
        feature,
        featureGroupName,
        featureGroupIndex: featureGroupName ? FeatureGroupEnum[featureGroupName] : 0,
      };
    });
    const highestFeatureGroup = maxBy(featureGroupList, 'featureGroupIndex');

    if (highestFeatureGroup && highestFeatureGroup.feature) {
      onUpsellRequest(`recordings.segments.${highestFeatureGroup.feature}`);
    }
  }, [onUpsellRequest, segment.unsupported_features]);
  const showButton = () => {
    setItemHovered(true);
    setItemRendered(true);
  };
  const hideButton = () => {
    setItemHovered(false);
  };
  const isScaleTrialDisabled = isAllowed('billing_new_scale', site);
  const isSiteInScaleTrialCohort =
    isAllowed('scale_trial_cohort', site) && site.effectivePlan === 'business';
  let isDisabled = segment.unsupported_features.length > 0;

  // apply translation only to default segments with negative index
  const getSegmentName = (segmentItem: SegmentWithUnsupportedFeature): string =>
    segmentItem.id < 0 ? t(segmentItem.name) : segmentItem.name;

  if (
    // This is error segment on the business plan case:
    // We want to disable it, when the scale trial is disabled
    segment.unsupported_features.length === 1 &&
    segment.unsupported_features.includes('filters.error')
  ) {
    isDisabled = isScaleTrialDisabled && !isSiteInScaleTrialCohort;
  }

  if (isDisabled) {
    return (
      <Tooltip
        content={t('common:recordings.segment-sidebar.upgrade-segments-tooltip')}
        placement="right"
        zIndex={800001}
      >
        <Item id={domId} hasButton={false}>
          <StyledLink as="button" to="" css={disabledStyles} onClick={handleUpsellClicked}>
            <TruncatedText>{getSegmentName(segment)}</TruncatedText>
          </StyledLink>
        </Item>
      </Tooltip>
    );
  }

  const url = `/sites/${siteId}/playbacks/list?segment=${segment.id}`;

  return (
    <Tooltip overflowRef={textRef} content={getSegmentName(segment)} zIndex={800001}>
      <Item
        id={domId}
        onMouseEnter={showButton}
        onMouseLeave={hideButton}
        hasButton={typeof renderButton === 'function'}
      >
        <StyledLink css={isSelected && selectedStyles} to={url} ref={textRef}>
          <TruncatedText>{getSegmentName(segment)}</TruncatedText>
          {hasScaleTrialBadge === true && (
            <>
              <BagdeSpacer aria-hidden="true" />
              <ScaleTrialBadge />
            </>
          )}
        </StyledLink>
        {typeof renderButton === 'function' && wasItemRendered && (
          <ButtonWrapper isVisible={isItemHovered}>{renderButton()}</ButtonWrapper>
        )}
      </Item>
    </Tooltip>
  );
};
